export type NavigationType = "push" | "replace" | "traverse";

export type NavigationTransition = {
  destination: string;
  id: number;
  navigationType: NavigationType;
  source: string;
};

const navigationTransitionTimeoutMs = 30_000;
const listeners = new Set<() => void>();

let nextTransitionId = 0;
let transition: NavigationTransition | null = null;
let transitionTimeout: ReturnType<typeof setTimeout> | null = null;

function normalizePathname(pathname: string): string {
  if (pathname === "/") {
    return pathname;
  }

  return pathname.replace(/\/+$/, "");
}

export function normalizeNavigationLocation(url: string, baseHref: string): string | null {
  const base = new URL(baseHref);
  const location = new URL(url, base);

  if (location.origin !== base.origin) {
    return null;
  }

  return `${normalizePathname(location.pathname)}${location.search}`;
}

function notifyListeners() {
  for (const listener of listeners) {
    listener();
  }
}

function cancelTransitionTimeout() {
  if (transitionTimeout !== null) {
    clearTimeout(transitionTimeout);
    transitionTimeout = null;
  }
}

export function getNavigationTransition(): NavigationTransition | null {
  return transition;
}

export function getServerNavigationTransition(): null {
  return null;
}

export function subscribeToNavigationTransition(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function completeNavigationTransition(id?: number): void {
  if (!transition || (id !== undefined && transition.id !== id)) {
    return;
  }

  cancelTransitionTimeout();
  transition = null;
  notifyListeners();
}

export function beginNavigationTransition(
  url: string,
  navigationType: NavigationType,
): number | null {
  if (typeof window === "undefined") {
    return null;
  }

  return beginNavigationTransitionAt(url, navigationType, window.location.href);
}

export function beginNavigationTransitionAt(
  url: string,
  navigationType: NavigationType,
  baseHref: string,
): number | null {
  const source = normalizeNavigationLocation(baseHref, baseHref);
  const destination = normalizeNavigationLocation(url, baseHref);

  if (!source || !destination || source === destination) {
    completeNavigationTransition();
    return null;
  }

  if (
    transition?.source === source
    && transition.destination === destination
    && transition.navigationType === navigationType
  ) {
    return transition.id;
  }

  cancelTransitionTimeout();
  const id = ++nextTransitionId;
  transition = { destination, id, navigationType, source };
  transitionTimeout = setTimeout(() => completeNavigationTransition(id), navigationTransitionTimeoutMs);
  notifyListeners();

  return id;
}

export function confirmLinkNavigation(url: string): number | null {
  if (typeof window === "undefined") {
    return null;
  }

  const destination = normalizeNavigationLocation(url, window.location.href);
  if (
    transition
    && destination === transition.destination
    && transition.navigationType === "push"
  ) {
    return transition.id;
  }

  return beginNavigationTransition(url, "push");
}

export function completeCommittedNavigation(url: string): void {
  if (!transition || typeof window === "undefined") {
    return;
  }

  completeCommittedNavigationAt(url, window.location.href);
}

export function completeCommittedNavigationAt(url: string, baseHref: string): void {
  if (!transition) {
    return;
  }

  const location = normalizeNavigationLocation(url, baseHref);
  if (location && location !== transition.source) {
    completeNavigationTransition(transition.id);
  }
}
